import { POPOUT_GAP_PX } from "~/components/popout/constants";

const VIEWPORT_RIGHT_PADDING = 16;

interface ComputeCascadeOffsetViewport {
  width: number;
}

interface CascadeOffset {
  x: number;
  y: number;
}

const NO_OFFSET: CascadeOffset = { x: 0, y: 0 };

/**
 * Side-by-side placement from the right edge. Returns the offset (px from the right edge,
 * moving leftward) for the next popout, given the widths of the popouts already open.
 *
 *   right                            ┌─── offset.x ───┐
 *   |..........[ prev2 ]..[ prev1 ]..[ NEW slot here ]|
 *
 * If the cumulative cascade would push the new popout past the left bound, clamps so the
 * new popout still sits within the viewport. The reducer evicts open popouts before
 * calling this when the cascade truly won't fit.
 */
export function computeCascadeOffset(
  previousOpenWidthsPx: readonly number[],
  newWidthPx: number,
  viewport: ComputeCascadeOffsetViewport,
): CascadeOffset {
  if (previousOpenWidthsPx.length === 0) {
    return NO_OFFSET;
  }
  const proposedOffset = previousOpenWidthsPx.reduce((sum, prevWidth) => sum + prevWidth + POPOUT_GAP_PX, 0);
  const maxOffset = Math.max(0, viewport.width - VIEWPORT_RIGHT_PADDING - newWidthPx);
  return { x: Math.min(proposedOffset, maxOffset), y: 0 };
}

/**
 * Whether a new popout of `newWidthPx` fits side-by-side with `previousOpenWidthsPx` at
 * `POPOUT_GAP_PX` spacing on a viewport of `viewport.width`. The reducer uses this to
 * decide whether to evict an existing open popout before adding the new one.
 */
export function fitsSideBySide(
  previousOpenWidthsPx: readonly number[],
  newWidthPx: number,
  viewport: ComputeCascadeOffsetViewport,
): boolean {
  const totalWidth =
    previousOpenWidthsPx.reduce((sum, w) => sum + w + POPOUT_GAP_PX, 0) + newWidthPx + VIEWPORT_RIGHT_PADDING;
  return totalWidth <= viewport.width;
}
